/* eslint-disable @typescript-eslint/no-unused-vars */
import i18nInst from 'src/i18n';
import {
  addEmails,
  addItems,
  checkDomainFormat,
  checkEmailFormat,
  checkExtension,
  checkFileBlockingExtension,
  checkNotification,
  checkReplacementNameOrigin,
  checkReplacementTextOrigin,
  checkToken,
  removeItem,
  showAddApprovedSenderExceedListLimitNotification,
  showAddApprovedSenderListErrNotification,
} from 'src/pages/Policy/utils/fns';
import {
  ActionEnum,
  ExchangeOnlinePolicyItem,
  FileBlockingFilter,
  Notification,
  PolicyFilters,
} from 'src/pages/Policy/utils/types';
import { PolicySubTypeEnum } from '@Types/index';
import { PolicyType } from '@Types/app';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { fbActionDataSelector } from './selectors';

export const createFileBlockingSlice = (
  setPolicy: any,
  getPolicy: any,
  getAll: any
) => {
  const { t } = i18nInst;
  return {
    setFbState: (payload: Partial<FileBlockingFilter>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          ...payload,
        },
      }));
    },
    setFbNtfState: (payload: Partial<Notification>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          notification: {
            ...state.tmFileblockings.notification,
            ...payload,
          },
        },
      }));
    },
    setFbExceptionFileTypeList: (selectedKeys: string[]) => {
      const fileTypes = getAll().config.fileTypes;
      const typeClasses = fileTypes[0].children.map((typeClass: any) => {
        return typeClass.key;
      });
      // add the total file type
      typeClasses.push(fileTypes[0].key);
      const newSelectedKeys = selectedKeys.filter(
        (key) => !typeClasses.includes(key)
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbExceptionFileTypeList: newSelectedKeys.join('|'),
        },
      }));
    },
    addExceptionFileExts: (exts: string[] | string) => {
      // const inSeparator = ';';
      // const outSeparator = '|';
      const currentState = getPolicy();
      const currentStore = getAll();
      const currentExts =
        currentState.tmFileblockings.fbExceptionFileExtensionList;
      const limit = currentStore.config.approvedFileExtensionCount;
      const { result, errList } = addItems(exts, currentExts, limit);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbExceptionFileExtensionList: result,
        },
      }));
      // process error list
      if (errList.length) {
        console.error('addExceptFileExts error list:', errList);
        const notImportExt = errList.map((item) => {
          return item.length > 70 ? `${item.slice(0, 70)}...` : item;
        });
        const notImportExtContent =
          notImportExt.length > 50
            ? `${notImportExt.slice(0, 50).join('\n')}\n...`
            : notImportExt.join('\n');
        const errorContent = `${t('policy.error.invalidFileExtContent')}\n${t(
          'policy.error.notImportExt'
        )} ${notImportExtContent}`;

        window.showNotification({
          titleMsg: t('policy.error.invalidFileExtTitle'),
          bodyMsg: errorContent,
          severity: 'error',
        });
      }
    },
    removeExceptionFileExt: (ext: string) => {
      const currentState = getPolicy();
      const {
        tmFileblockings: { fbExceptionFileExtensionList },
      } = currentState;
      const result = removeItem(ext, fbExceptionFileExtensionList);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbExceptionFileExtensionList: result,
        },
      }));
    },
    addExceptionFileNames: (exts: string[] | string) => {
      const currentState = getPolicy();
      const currentExts = currentState.tmFileblockings.fbExceptionFileNameList;
      const currentStore = getAll();
      const listLimit = currentStore.config.approvedListMaxCount;
      const { result, errList } = addItems(
        exts,
        currentExts,
        listLimit,
        -1,
        checkFileBlockingExtension
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbExceptionFileNameList: result,
        },
      }));
      // process error list
      if (errList.length) {
        console.error('addExceptFileExts error list:', errList);
        const notImportExt = errList.map((item) => {
          return item.length > 70 ? `${item.slice(0, 70)}...` : item;
        });
        const notImportExtContent =
          notImportExt.length > 50
            ? `${notImportExt.slice(0, 50).join('\n')}\n...`
            : notImportExt.join('\n');
        const errorContent = `${t(
          'policy.error.invalidFileNameContentTitle'
        )}\n${t('policy.error.notImportExt')} ${notImportExtContent}`;
        window.showNotification({
          titleMsg: t('policy.error.invalidFileNameTitle'),
          bodyMsg: errorContent,
          severity: 'error',
        });
      }
    },
    removeExceptionFileName: (ext: string) => {
      const currentState = getPolicy();
      const {
        tmFileblockings: { fbExceptionFileNameList },
      } = currentState;

      const result = removeItem(ext, fbExceptionFileNameList);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbExceptionFileNameList: result,
        },
      }));
    },
    setFbBlockFileTypeList: (selectedKeys: string[]) => {
      const fileTypes = getAll().config.fileTypes;
      const typeClasses = fileTypes[0].children.map((typeClass: any) => {
        return typeClass.key;
      });
      // add the total file type
      typeClasses.push(fileTypes[0].key);
      const newSelectedKeys = selectedKeys.filter(
        (key) => !typeClasses.includes(key)
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbBlockFileTypeList: newSelectedKeys.join('|'),
        },
      }));
    },
    setFbIncomingRuleBlockFileTypeList: (selectedKeys: string[]) => {
      const fileTypes = getAll().config.fileTypes;
      const typeClasses = fileTypes[0].children.map((typeClass: any) => {
        return typeClass.key;
      });
      // add the total file type
      typeClasses.push(fileTypes[0].key);
      const newSelectedKeys = selectedKeys.filter(
        (key) => !typeClasses.includes(key)
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbIncomingRuleBlockFileTypeList: newSelectedKeys.join('|'),
        },
      }));
    },
    addBlockFileExts: (exts: string[] | string) => {
      // const inSeparator = ';';
      // const outSeparator = '|';
      const currentState = getPolicy();
      const currentStore = getAll();
      const currentExts = currentState.tmFileblockings.fbBlockFileExtensionList;
      const limit = currentStore.config.blockFileExtensionCount;
      const { result, errList } = addItems(exts, currentExts, limit);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbBlockFileExtensionList: result,
        },
      }));
      // process error list
      if (errList.length) {
        console.error('addBlockFileExts error list:', errList);
        const notImportExt = errList.map((item) => {
          return item.length > 70 ? `${item.slice(0, 70)}...` : item;
        });
        const notImportExtContent =
          notImportExt.length > 50
            ? `${notImportExt.slice(0, 50).join('\n')}\n...`
            : notImportExt.join('\n');
        const errorContent = `${t('policy.error.invalidFileExtContent')}\n${t(
          'policy.error.notImportExt'
        )} ${notImportExtContent}`;

        window.showNotification({
          titleMsg: t('policy.error.invalidFileExtTitle'),
          bodyMsg: errorContent,
          severity: 'error',
        });
      }
    },
    removeBlockFileExt: (ext: string) => {
      const currentState = getPolicy();
      const {
        tmFileblockings: { fbBlockFileExtensionList },
      } = currentState;
      const result = removeItem(ext, fbBlockFileExtensionList);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbBlockFileExtensionList: result,
        },
      }));
    },
    addBlockFileNames: (exts: string[] | string) => {
      const currentState = getPolicy();
      const currentExts = currentState.tmFileblockings.fbBlockFileNameList;
      const currentStore = getAll();
      const listLimit = currentStore.config.approvedListMaxCount;
      const { result, errList } = addItems(
        exts,
        currentExts,
        listLimit,
        -1,
        checkFileBlockingExtension
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbBlockFileNameList: result,
        },
      }));
      // process error list
      if (errList.length) {
        console.log('addBlockFileNames error list:', errList);
        const notImportExt = errList.map((item) => {
          return item.length > 70 ? `${item.slice(0, 70)}...` : item;
        });
        const notImportExtContent =
          notImportExt.length > 50
            ? `${notImportExt.slice(0, 50).join('\n')}\n...`
            : notImportExt.join('\n');
        const errorContent = `${t(
          'policy.error.invalidFileNameContentTitle'
        )}\n${t('policy.error.notImportExt')} ${notImportExtContent}`;
        window.showNotification({
          titleMsg: t('policy.error.invalidFileNameTitle'),
          bodyMsg: errorContent,
          severity: 'error',
        });
      }
    },
    removeBlockFileName: (ext: string) => {
      const currentState = getPolicy();
      const {
        tmFileblockings: { fbBlockFileNameList },
      } = currentState;

      const result = removeItem(ext, fbBlockFileNameList);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbBlockFileNameList: result,
        },
      }));
    },
    addAdditionalBlockFileExts: (exts: string[] | string) => {
      // const inSeparator = ';';
      // const outSeparator = '|';
      const currentState = getPolicy();
      const currentStore = getAll();
      const currentExts =
        currentState.tmFileblockings.fbIncomingRuleBlockFileExtensionList;
      const limit = currentStore.config.blockFileExtensionCount;
      const { result, errList } = addItems(exts, currentExts, limit);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbIncomingRuleBlockFileExtensionList: result,
        },
      }));
      // process error list
      if (errList.length) {
        console.error('add additionalBlockFileExts error list:', errList);
        const notImportExt = errList.map((item) => {
          return item.length > 70 ? `${item.slice(0, 70)}...` : item;
        });
        const notImportExtContent =
          notImportExt.length > 50
            ? `${notImportExt.slice(0, 50).join('\n')}\n...`
            : notImportExt.join('\n');
        const errorContent = `${t('policy.error.invalidFileExtContent')}\n${t(
          'policy.error.notImportExt'
        )} ${notImportExtContent}`;

        window.showNotification({
          titleMsg: t('policy.error.invalidFileExtTitle'),
          bodyMsg: errorContent,
          severity: 'error',
        });
      }
    },
    removeAdditionalBlockFileExt: (ext: string) => {
      const currentState = getPolicy();
      const {
        tmFileblockings: { fbIncomingRuleBlockFileExtensionList },
      } = currentState;
      const result = removeItem(ext, fbIncomingRuleBlockFileExtensionList);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbIncomingRuleBlockFileExtensionList: result,
        },
      }));
    },
    addAdditionalBlockFileNames: (exts: string[] | string) => {
      const currentState = getPolicy();
      const currentExts =
        currentState.tmFileblockings.fbIncomingRuleBlockFileNameList;
      const currentStore = getAll();
      const listLimit = currentStore.config.approvedListMaxCount;
      const { result, errList } = addItems(
        exts,
        currentExts,
        listLimit,
        -1,
        checkFileBlockingExtension
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbIncomingRuleBlockFileNameList: result,
        },
      }));
      // process error list
      if (errList.length) {
        console.log('addBlockFileNames error list:', errList);
        const notImportExt = errList.map((item) => {
          return item.length > 70 ? `${item.slice(0, 70)}...` : item;
        });
        const notImportExtContent =
          notImportExt.length > 50
            ? `${notImportExt.slice(0, 50).join('\n')}\n...`
            : notImportExt.join('\n');
        const errorContent = `${t(
          'policy.error.invalidFileNameContentTitle'
        )}\n${t('policy.error.notImportExt')} ${notImportExtContent}`;
        window.showNotification({
          titleMsg: t('policy.error.invalidFileNameTitle'),
          bodyMsg: errorContent,
          severity: 'error',
        });
      }
    },
    removeAddtionalBlockFileName: (ext: string) => {
      const currentState = getPolicy();
      const {
        tmFileblockings: { fbIncomingRuleBlockFileNameList },
      } = currentState;

      const result = removeItem(ext, fbIncomingRuleBlockFileNameList);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbIncomingRuleBlockFileNameList: result,
        },
      }));
    },
    addFbApprovedSender: (email: string | string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmFileblockings: { fbApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const includeDomain = true;
      const supportWildcard = true;
      const separator = ';';
      const { entriesStr, errList } = addEmails(
        email,
        fbApprovedSenderEmailEntries,
        includeDomain,
        limitation.singleApprovedListMaxCount,
        separator,
        -1,
        supportWildcard
      );
      // when add approved user, exceed list limit, will popup import exceed limit error message
      // if (exceedListLimit) {
      //   showAddApprovedSenderExceedListLimitNotification(
      //     limitation.approvedListMaxCount
      //   );
      // }
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbApprovedSenderEmailEntries: entriesStr,
        },
      }));
      // process error list
      if (errList.length) {
        console.error('addFbApprovedSender error list:', errList);
        showAddApprovedSenderListErrNotification(errList);
      }
    },
    removeFbApprovedSender: (email: string) => {
      const deleteEmail = email.trim();
      if (deleteEmail === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmFileblockings: { fbApprovedSenderEmailEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteEmail,
        fbApprovedSenderEmailEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbApprovedSenderEmailEntries: result,
        },
      }));
    },
    setFbActionType: (action: ActionEnum) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbActionType: action,
        },
      }));
    },
    setFbNotifyType: (e: React.ChangeEvent<HTMLInputElement>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmFileblockings: {
          ...state.tmFileblockings,
          fbNotifyType: e.target.checked ? 1 : 0,
        },
      }));
    },
    importFbApprovedSender: (emails: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmFileblockings: { fbApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const separator = ';';
      const validEmails = emails.filter((email) => email.length > 0);
      const existedEntries = fbApprovedSenderEmailEntries
        .split(separator)
        .filter((entry: string) => entry.length > 0);
      if (
        validEmails.length + existedEntries.length >
        limitation.approvedListMaxCount
      ) {
        // exceed limit, popup error message, do not set any senders in store
        showAddApprovedSenderExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
      } else {
        currentStore.addFbApprovedSender(validEmails);
      }
    },
    validateFileBlocking: () => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const filter = currentState.tmFileblockings;
      const actionData = fbActionDataSelector(currentStore);
      currentStore.replaceInvalidAction(actionData);

      const result = {
        [PolicyFilters.tmFileblockings]: {},
      };
      const { policyType } = filter;
      const {
        notifyMessageMaxLength,
        notifySubjectMaxLength,
        fileReplacement,
      } = currentStore.config.limitation;
      let checkResult: any;
      // check notification
      checkResult = checkNotification(
        filter.notification,
        notifyMessageMaxLength,
        notifySubjectMaxLength
      );
      if (checkResult) {
        // return directly when meet an error
        result[PolicyFilters.tmFileblockings] = checkResult;
        return result;
      }
      // check replacement name and text
      const replacementNameKeys = ['fbReplacementFileName'];
      for (const nameKey of replacementNameKeys) {
        checkResult = checkReplacementNameOrigin(filter[nameKey]);
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmFileblockings] = {
            [nameKey]: checkResult,
          };
          return result;
        }
      }
      const replacementTextKeys = ['fbReplacementText'];
      for (const textKey of replacementTextKeys) {
        checkResult = checkReplacementTextOrigin(
          filter[textKey],
          fileReplacement
        );
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmFileblockings] = {
            [textKey]: checkResult,
          };
          return result;
        }
      }
      // check token
      if (
        ![PolicyTypeEnum.Exchange, PolicyTypeEnum.Gmail].includes(policyType)
      ) {
        checkToken({
          keyValue: filter.fbReplacementText,
          keyName: 'fbReplacementText',
        });
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmFileblockings] = checkResult;
          return result;
        }
      }

      // Check if at least one blocking list is selected when using "Block Specific Files"
      if (
        filter.fbEnabled === 1 && // File Blocking is enabled
        filter.fbScanningType === 1 && // "Block Specific Files" is selected
        !filter.fbBlockFileTypeEnabled && // File types to block is not checked
        !filter.fbBlockFileExtensionEnabled && // File extensions to block is not checked
        !filter.fbBlockFileNameEnabled // File names to block is not checked
      ) {
        result[PolicyFilters.tmFileblockings] = {
          fbBlockingList: {
            title: i18nInst.t(
              'policy:policy.enableFileBlocking.error.noBlockingListSelectedTitle'
            ),
            content: i18nInst.t(
              'policy:policy.enableFileBlocking.error.noBlockingListSelectedContent'
            ),
          },
        };
        return result;
      }

      return;
    },
  };
};
